import { formatUnits } from "viem";
import { getConfig } from "../constants/config";
import tokenService from "../../common/tokenService";

/**
 * Calculates the total input amount across all paths in a route.
 *
 * @param {Array} route - Route array from getRoute
 *
 * @returns {Promise<Object>} Route input details
 * @returns {bigint} .amountIn - Total input amount in raw units
 * @returns {string} .amountInFormatted - Total input amount formatted with token decimals
 * @returns {string} .tokenIn - Input token address
 * @returns {string} .tokenOut - Output token address
 *
 * @throws {Error} If route is invalid or missing
 *
 * @example
 * // Example 1: Get total input for a single-path route
 * const input = await getTotalAmountIn(route);
 * console.log(input.amountInFormatted); // "1.0"
 *
 *
 * @note
 * - Returns undefined values if route is invalid
 * - Sums amountIn from first step of each path for total input
 * - Uses token decimals from config for formatting
 */
export const getTotalAmountIn = async (route) => {
  if (!route || !Array.isArray(route)) {
    console.error("Invalid or missing `route`: Must be an array.");
    return {
      amountIn: undefined,
      amountInFormatted: undefined,
      tokenIn: undefined,
      tokenOut: undefined,
    };
  }

  // Get config for non-token data
  const config = getConfig();
  const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
    config;

  // Get updated token list with user's configuration applied
  const tokens = await tokenService.getAsyncTokenList(
    chainId,
    additionalTokens,
    overrideDefaultTokens,
    nativeAddress
  );

  const tokenIn = tokens[route[0][0].tokenIn];
  const tokenOut = tokens[route[0].at(-1).tokenOut];

  let totalAmountIn = 0n;

  for (let i = 0; i < route.length; i++) {
    totalAmountIn = totalAmountIn + BigInt(route[i][0]?.amountIn);
  }

  return {
    amountIn: totalAmountIn,
    amountInFormatted: formatUnits(totalAmountIn, tokenIn.attributes.decimals),
    tokenIn: tokenIn.attributes.address,
    tokenOut: tokenOut.attributes.address,
  };
};
